import { isExperimentName } from './registry';
import { clearAllOverrides, setOverride } from './overrides';

const OVERRIDE_QUERY_PARAM_PREFIX = 'xp_';
const CLEAR_QUERY_PARAM = 'clearExperiments';

/**
 * Custom event fired after `processExperimentParamsFromURL` writes or clears
 * override cookies. `useTreatment` subscribes to this so a hook that mounted
 * before AppBootstrap's effect ran can pick up the freshly-written override
 * without a full reload.
 */
export const EXPERIMENT_OVERRIDES_CHANGED_EVENT = 'hotpads:experiment-overrides-changed';

/**
 * Process experiment-override URL params on client mount, persist to cookies,
 * and clean the URL.
 *
 * - `?xp_<EXPERIMENT_NAME>=<treatment>` — force a treatment (also accepts `control`)
 * - `?clearExperiments=true` — clear every override cookie
 *
 * The `xp_` prefix is deliberately distinct from the feature-flag system's
 * `ff_` prefix so the two systems share no cookies or URL params and can't
 * clobber each other.
 *
 * Unknown experiment names (not in the registry) are silently dropped from the
 * URL without persisting, so stale links don't pollute the cookie jar.
 */
export function processExperimentParamsFromURL(): void {
  if (typeof window === 'undefined' || typeof URLSearchParams === 'undefined') {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const paramsToDelete = new Set<string>();
  const clearRawValue = searchParams.get(CLEAR_QUERY_PARAM);
  // Treat anything truthy ('true', '1', '') as a clear request, but reject
  // explicit falsy values ('false', '0', 'no') so a `?clearExperiments=false`
  // debug URL doesn't accidentally wipe every override.
  const shouldClear =
    clearRawValue !== null &&
    !['false', '0', 'no', 'off'].includes(clearRawValue.trim().toLowerCase());

  if (shouldClear) {
    paramsToDelete.add(CLEAR_QUERY_PARAM);
    clearAllOverrides();
  } else if (clearRawValue !== null) {
    paramsToDelete.add(CLEAR_QUERY_PARAM);
  }

  let overrideParamSeen = false;
  for (const [paramName, rawValue] of searchParams.entries()) {
    if (!paramName.startsWith(OVERRIDE_QUERY_PARAM_PREFIX)) continue;
    overrideParamSeen = true;

    paramsToDelete.add(paramName);
    const experimentName = paramName.slice(OVERRIDE_QUERY_PARAM_PREFIX.length);
    if (!isExperimentName(experimentName)) continue;

    const trimmed = rawValue.trim();
    if (trimmed.length === 0) continue;

    setOverride(experimentName, trimmed);
  }

  // `?clearExperiments=true&xp_FOO=variant1` is ambiguous: clear runs first,
  // then setOverride re-applies the named overrides — net effect is "reset
  // every other override and apply just this one". Log so QA isn't surprised
  // when overrides they tried to clear come back via the same URL.
  if (shouldClear && overrideParamSeen) {
    // eslint-disable-next-line no-console
    console.warn(
      '[experiments] ?clearExperiments=true was combined with xp_ overrides; ' +
        'overrides in the same URL are re-applied after the clear.',
    );
  }

  if (paramsToDelete.size === 0) return;

  for (const param of paramsToDelete) {
    searchParams.delete(param);
  }
  const nextSearch = searchParams.toString();
  // Preserve `window.location.hash` so deep links like /listing#contact aren't
  // silently truncated after the override URL is cleaned.
  const hash = window.location.hash;
  const nextUrl =
    (nextSearch ? `${window.location.pathname}?${nextSearch}` : window.location.pathname) + hash;
  window.history.replaceState(null, '', nextUrl);

  window.dispatchEvent(new Event(EXPERIMENT_OVERRIDES_CHANGED_EVENT));
}
